/**
 * Line Chart Widget - Muestra tendencias temporales
 *
 * Widget para mostrar métricas de tendencia con series temporales.
 */

import React from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import {
  LineChart as RechartsLine,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from 'recharts';
import { WidgetWrapper } from './WidgetWrapper';
import {useChartPalette} from "../../../theme/chartPalette";
import {dayFirstLocale} from "../../../common/utils";

export interface TrendData {
  date: string;
  count: number;
}

export interface LineChartProps {
  title: string;
  data: TrendData[];
  loading?: boolean;
  computing?: boolean;
  error?: string | null;
  onRefresh?: () => void;
  icon?: React.ReactNode;
  color?: string;
  granularity?: 'day' | 'week' | 'month';
  /**
   * El locale de los nombres de mes y de día, con el día primero
   * (dayFirstLocale): WidgetRenderer pasa el del idioma de la app. Por defecto,
   * el castellano.
   */
  locale?: string;
}

export const LineChart: React.FC<LineChartProps> = ({
  title,
  data,
  loading = false,
  computing = false,
  error = null,
  onRefresh,
  icon,
  color,
  granularity = 'day',
  locale = dayFirstLocale('es'),
}) => {
  const paleta = useChartPalette();
  const trazo = color ?? paleta.color(0);
  const theme = useTheme();

  // Formatear fechas para el eje X
  const formatDate = (dateStr: string): string => {
    const date = new Date(dateStr);
    if (granularity === 'month') {
      return date.toLocaleDateString(locale, { month: 'short', year: '2-digit' });
    }
    if (granularity === 'week') {
      return date.toLocaleDateString(locale, { month: 'short', day: 'numeric' });
    }
    return date.toLocaleDateString(locale, { month: 'short', day: 'numeric' });
  };

  // Preparar datos para recharts
  const chartData = data.map((item) => ({
    date: item.date,
    count: item.count,
    formattedDate: formatDate(item.date),
  }));

  // Calcular total y promedio
  const total = data.reduce((sum, item) => sum + item.count, 0);
  const average = data.length > 0 ? Math.round(total / data.length) : 0;

  // Tooltip personalizado
  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const item = payload[0].payload;
      const date = new Date(item.date);
      const formattedDate = date.toLocaleDateString(locale, {
        weekday: 'short',
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });

      return (
        <Box
          sx={{
            bgcolor: 'background.paper',
            p: 1.5,
            borderRadius: 1,
            boxShadow: theme.shadows[3],
            border: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Typography variant="body2" fontWeight="medium">
            {formattedDate}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {item.count.toLocaleString()}
          </Typography>
        </Box>
      );
    }
    return null;
  };

  return (
    <WidgetWrapper
      title={title}
      loading={loading}
      computing={computing}
      error={error}
      onRefresh={onRefresh}
      icon={icon}
      minHeight={280}
    >
      <Box sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
        {data.length === 0 ? (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 1 }}>
            <Typography variant="body2" color="text.secondary" textAlign="center">
              No hay datos disponibles
            </Typography>
          </Box>
        ) : (
          <>
            {/* Chart container */}
            <Box sx={{ height: 200, width: '100%', px: 1 }}>
              <ResponsiveContainer width="100%" height="100%">
                <RechartsLine data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke={theme.palette.divider}
                    vertical={false}
                  />
                  <XAxis
                    dataKey="formattedDate"
                    tick={{ fontSize: 11, fill: theme.palette.text.secondary }}
                    tickLine={false}
                    axisLine={{ stroke: theme.palette.divider }}
                    interval="preserveStartEnd"
                  />
                  <YAxis
                    tick={{ fontSize: 11, fill: theme.palette.text.secondary }}
                    tickLine={false}
                    axisLine={false}
                    tickFormatter={(value) => value.toLocaleString()}
                    width={45}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Line
                    type="monotone"
                    dataKey="count"
                    stroke={trazo}
                    strokeWidth={2}
                    dot={false}
                    activeDot={{ r: 6, fill: trazo }}
                  />
                </RechartsLine>
              </ResponsiveContainer>
            </Box>

            {/* Stats summary */}
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                gap: 4,
                px: 2,
                pt: 1,
              }}
            >
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h6" fontWeight="bold" color="text.primary">
                  {total.toLocaleString()}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Total
                </Typography>
              </Box>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h6" fontWeight="bold" color="text.primary">
                  {average.toLocaleString()}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Promedio
                </Typography>
              </Box>
            </Box>
          </>
        )}
      </Box>
    </WidgetWrapper>
  );
};

export default LineChart;
